import type { ReactElement } from 'react';
import { parseEntityRef, RELATION_OWNED_BY } from '@backstage/catalog-model';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  EntityDisplayName,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';
import { makeStyles } from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUploadOutlined';
import MenuBookIcon from '@material-ui/icons/MenuBookOutlined';
import CodeIcon from '@material-ui/icons/Code';
import CallMergeIcon from '@material-ui/icons/CallMerge';
import PlayCircleIcon from '@material-ui/icons/PlayCircleOutline';
import HttpIcon from '@material-ui/icons/Http';
import DescriptionIcon from '@material-ui/icons/DescriptionOutlined';
import AccountTreeIcon from '@material-ui/icons/AccountTreeOutlined';
import PeopleIcon from '@material-ui/icons/PeopleOutline';
import LinkIcon from '@material-ui/icons/Link';
import ReportProblemIcon from '@material-ui/icons/ReportProblemOutlined';
import SubjectIcon from '@material-ui/icons/Subject';
import ShowChartIcon from '@material-ui/icons/ShowChart';
import { formatStarted } from '../pipeline/format';
import { outcomeOf } from '../pipeline/outcome';
import { OutcomePill } from '../pipeline/OutcomePill';
import { useComponentPage } from './ComponentPageContext';
import { OverviewRow, type RowProps } from './OverviewRow';
import { targetsOf } from './relations';
import { techDocsOf, useTechDocsFacts } from './useTechDocsFacts';
import { STATUS_PAGE } from './serviceStatus';
import { observabilityOf } from './observability';

const useStyles = makeStyles(theme => ({
  columns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: theme.spacing(2),
  },
  column: {
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    padding: theme.spacing(1.5, 2),
    minWidth: 0,
  },
  heading: {
    margin: theme.spacing(0, 0, 0.5),
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--ui-text-muted)',
  },
}));

const since = (iso: string | null | undefined) =>
  iso ? formatStarted(iso, Date.now())?.label : undefined;

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/**
 * The Overview's three columns (specs/component-page.md): what it takes to
 * operate the component, to build it, and to understand it. A row with nothing
 * behind it is left out; a column with no rows is left out — a library that
 * never deploys has no Operate column rather than an empty one.
 */
export const OverviewColumns = () => {
  const classes = useStyles();
  const { entity, slug, pipeline, pullRequests, catalog, status } =
    useComponentPage();
  const docs = useTechDocsFacts(entity);
  const entityRoute = useRouteRef(entityRouteRef);
  const base = entityRoute({
    kind: entity.kind.toLowerCase(),
    namespace: (entity.metadata.namespace ?? 'default').toLowerCase(),
    name: entity.metadata.name,
  });
  // A subcomponent's docs are its main component's site (DX-628).
  const site = techDocsOf(entity);
  const docsRoot = site && `/docs/${site.namespace}/${site.kind}/${site.name}`;

  const operate: RowProps[] = [];
  const build: RowProps[] = [];
  const understand: RowProps[] = [];

  // Operate ---------------------------------------------------------------
  const hasDeployWorkflow =
    !!pipeline.data && pipeline.data.deployWorkflows.length > 0;
  if (slug && (pipeline.loading || pipeline.error || hasDeployWorkflow)) {
    const run = pipeline.data?.latestDeployment;
    let sub: RowProps['sub'] = 'No deploy yet';
    if (pipeline.error) sub = "Couldn't load";
    else if (run)
      sub = [since(run.createdAt), run.headBranch].filter(Boolean).join(' · ');
    operate.push({
      icon: <CloudUploadIcon fontSize="small" />,
      title: 'Deployments',
      sub,
      loading: pipeline.loading,
      to: `${base}/ci-cd`,
    });
  }
  // Incidents — only for a component the status service measures, as the facts
  // bar's health and uptime are: no measurement, no row.
  if (status.data?.measured) {
    const { incidents, url, windowDays } = status.data;
    const [newest, ...moreOpen] = incidents.open;
    let incidentsSub = `None in ${windowDays} days`;
    if (newest)
      incidentsSub = `${incidents.open.length} open · ${newest.title}`;
    else if (incidents.inWindow > 0) {
      incidentsSub = `None open · ${incidents.inWindow} in ${windowDays} days`;
    }
    // One open incident is the place to go; otherwise the service's page, and
    // for an internal service — which has no public page — the admin's list.
    let incidentsTo = url ?? `${STATUS_PAGE}/admin/incidents`;
    if (newest && moreOpen.length === 0) {
      incidentsTo = newest.url ?? newest.adminUrl;
    }
    operate.push({
      icon: <ReportProblemIcon fontSize="small" />,
      title: 'Incidents',
      sub: incidentsSub,
      to: incidentsTo,
      external: true,
    });
  }
  // Logs and metrics — only for a component whose annotation names them.
  const { logs, metrics } = observabilityOf(entity);
  if (logs) {
    operate.push({
      icon: <SubjectIcon fontSize="small" />,
      title: 'Logs',
      sub: `Grafana · stack ${logs.stack}`,
      to: logs.url,
      external: true,
    });
  }
  if (metrics) {
    operate.push({
      icon: <ShowChartIcon fontSize="small" />,
      title: 'Metrics',
      sub: `Grafana · dashboards tagged ${metrics.tag}`,
      to: metrics.url,
      external: true,
    });
  }
  if (docs.runbook && docsRoot) {
    operate.push({
      icon: <MenuBookIcon fontSize="small" />,
      title: 'Runbook',
      sub: docs.runbook.more
        ? `${docs.runbook.title} · +${docs.runbook.more} more`
        : docs.runbook.title,
      to: `${docsRoot}/${docs.runbook.location}`,
    });
  }

  // Build -----------------------------------------------------------------
  if (slug) {
    build.push({
      icon: <CodeIcon fontSize="small" />,
      title: 'Repository',
      sub: slug,
      to: `https://github.com/${slug}`,
      external: true,
    });
    const prs = pullRequests.data;
    const waiting = prs?.pullRequests.filter(pr => pr.needsReview).length ?? 0;
    let prSub = "Couldn't load";
    if (prs) {
      // Past a page, both figures are floors: the waiting ones were counted
      // among the pull requests that were listed.
      const more = prs.complete === false ? '+' : '';
      const verb = waiting === 1 && !more ? 'needs' : 'need';
      const review = waiting ? ` · ${waiting}${more} ${verb} review` : '';
      prSub = `${prs.total}${more} open${review}`;
    }
    build.push({
      icon: <CallMergeIcon fontSize="small" />,
      title: 'Pull requests',
      sub: prSub,
      loading: pullRequests.loading,
      to: `${base}/pull-requests`,
    });
    const onDefault = pipeline.data?.latestOnDefaultBranch;
    let ciSub: RowProps['sub'] = pipeline.error
      ? "Couldn't load"
      : `No run on ${pipeline.data?.defaultBranch ?? 'the default branch'} yet`;
    if (onDefault) {
      ciSub = (
        <>
          <OutcomePill outcome={outcomeOf(onDefault)} />{' '}
          {pipeline.data?.defaultBranch ?? onDefault.headBranch}
        </>
      );
    }
    build.push({
      icon: <PlayCircleIcon fontSize="small" />,
      title: 'CI/CD',
      sub: ciSub,
      loading: pipeline.loading,
      to: `${base}/ci-cd`,
    });
  }
  const [api] = catalog.providedApis;
  if (api) {
    build.push({
      icon: <HttpIcon fontSize="small" />,
      title: 'API contract',
      sub: [api.title ?? api.name, api.type].filter(Boolean).join(' · '),
      to: `${base}/api`,
    });
  }

  // Understand ------------------------------------------------------------
  if (site) {
    let docsSub = 'TechDocs';
    if (docs.builtAt)
      docsSub = `TechDocs · built ${since(docs.builtAt.toISOString())}`;
    else if (docs.notBuilt) docsSub = 'TechDocs · not built yet';
    else if (docs.error) docsSub = "TechDocs · couldn't load";
    understand.push({
      icon: <DescriptionIcon fontSize="small" />,
      title: 'Documentation',
      sub: docsSub,
      loading: docs.loading,
      to: `${base}/docs`,
    });
  }
  const relations = catalog.relations;
  let depsSub: string | undefined;
  if (relations) {
    depsSub = `${relations.inbound.length} in · ${relations.outbound.length} out`;
  } else if (catalog.error) {
    depsSub = "Couldn't load";
  }
  understand.push({
    icon: <AccountTreeIcon fontSize="small" />,
    title: 'Dependencies',
    sub: depsSub,
    loading: catalog.loading,
    to: `${base}/dependencies`,
  });
  const owner = targetsOf(entity, RELATION_OWNED_BY)[0];
  if (owner) {
    const ref = parseEntityRef(owner);
    understand.push({
      icon: <PeopleIcon fontSize="small" />,
      title: 'Ownership',
      // The row is the link; its line only names the team.
      sub: <EntityDisplayName entityRef={owner} hideIcon disableTooltip />,
      to: entityRoute({
        kind: ref.kind.toLowerCase(),
        namespace: ref.namespace.toLowerCase(),
        name: ref.name,
      }),
    });
  }
  for (const link of entity.metadata.links ?? []) {
    understand.push({
      icon: <LinkIcon fontSize="small" />,
      title: link.title ?? hostOf(link.url),
      sub: hostOf(link.url),
      to: link.url,
      external: true,
    });
  }

  const columns: [string, RowProps[]][] = [
    ['Operate', operate],
    ['Build', build],
    ['Understand', understand],
  ];
  return (
    <div className={classes.columns}>
      {columns
        .filter(([, rows]) => rows.length > 0)
        .map(([heading, rows]): ReactElement => (
          <section
            key={heading}
            className={classes.column}
            role="group"
            aria-label={heading}
          >
            <h2 className={classes.heading}>{heading}</h2>
            {rows.map(row => (
              <OverviewRow key={row.title + row.to} {...row} />
            ))}
          </section>
        ))}
    </div>
  );
};
